import React from "react";
import { LANGUAGES, useI18n } from "../i18n";

/**
 * A chevron drawn by us rather than relying on the browser's own — Safari
 * removes it anyway once the select's native appearance is turned off below.
 */
const chevron = (color) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='${encodeURIComponent(
    color
  )}'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z' clip-rule='evenodd'/%3E%3C/svg%3E")`;

/**
 * This device's language: a real dropdown, so the three choices are visible
 * and nothing can be mistyped. The choice is remembered on the device.
 *
 * `appearance-none` plus a hand-drawn chevron, rather than the select's own —
 * Safari's native select chrome does not reliably honour
 * `padding-inline-start` for where the selected value's text starts, so the
 * globe emoji ended up drawn on top of it. Turning appearance off hands the
 * whole box to our own CSS, which every browser then renders the same way.
 */
export default function LanguageSelect({ tone = "light", className = "" }) {
  const { lang, setLang, dir, t } = useI18n();
  const arrowColor = tone === "dark" ? "#cbd5e1" : "#6b7280";

  return (
    <label className={`relative inline-flex items-center shrink-0 ${className}`}>
      <span className="sr-only">{t("common.language")}</span>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute start-2.5 text-sm"
      >
        🌐
      </span>
      <select
        value={lang}
        onChange={(event) => setLang(event.target.value)}
        className={`h-10 ps-8 pe-8 rounded-lg text-sm font-medium border appearance-none bg-no-repeat focus:outline-none focus:ring-2 focus:ring-primary-500 ${
          tone === "dark"
            ? "bg-slate-800 border-slate-700 text-slate-100"
            : "bg-white border-gray-300 text-gray-700"
        }`}
        style={{
          backgroundImage: chevron(arrowColor),
          // Author padding on the select is what start-8/end-2 above already
          // reserve; the arrow itself still has to be placed by physical
          // side, which flips with the language's own direction
          backgroundPosition: `${dir === "rtl" ? "left" : "right"} 0.5rem center`,
          backgroundSize: "1rem 1rem",
        }}
      >
        {LANGUAGES.map((language) => (
          <option key={language.code} value={language.code}>
            {language.label}
          </option>
        ))}
      </select>
    </label>
  );
}
